import React, { useContext, useMemo, useState } from 'react';
import { DutyPlannerContext } from './DutyPlanner';
import { ProgressBar } from '../../ui-kit';
import { IconCalendarXmark } from '../../ui-kit/icons';
import { displayIntervals } from './timeline-helper';
import SkillIcon from '../../general/SkillIcon';
import { ButtonGroup, Button, ToggleButtonGroup, ToggleButton } from '../../ui-kit';
import { linkToNewDutyTransaction } from '../../../utils/dutyTransactions';
import IntervalInformationPopup from './IntervalInformationPopup';
import moment from 'moment';

const MiniTimeline = ({ intervals, periodStartTime, periodEndTime }) => (
  <ProgressBar className={'timeline mini my-1'}>
    {displayIntervals(intervals || [], periodStartTime, periodEndTime)}
  </ProgressBar>
);

const MultiFunctionSelector = ({ functions, assignToFunction, user }) => {
  const [selectedFunctions, setSelectedFunctions] = useState([]);

  const assignSelectedFunctions = () => {
    assignToFunction(selectedFunctions, user, false);
    setSelectedFunctions([]);
  };

  return (
    <>
      <ToggleButtonGroup
        vertical
        className="w-100 mb-2"
        size="sm"
        type="checkbox"
        value={selectedFunctions}
        onChange={values => setSelectedFunctions(values)}
      >
        {functions.map(f => (
          <ToggleButton
            id={`function-btn-${user.id}-${f.id}`}
            variant="outline-secondary"
            className="duty-function"
            type="checkbox"
            key={f.id}
            value={f.id}
          >
            <SkillIcon skill={f} /> {f.name}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <div className="hstack">
        <Button
          variant="primary"
          size="sm"
          className="ms-auto"
          onClick={assignSelectedFunctions}
          disabled={selectedFunctions.length === 0}
        >
          {I18n.t('assign')}
        </Button>
      </div>
    </>
  );
};

const SingleFunctionSelector = ({ functions, assignToFunction, user }) => (
  <ButtonGroup vertical className="w-100 mb-2" size="sm">
    {functions.map(f => (
      <Button
        variant="outline-secondary"
        className="duty-function"
        key={f.id}
        onClick={() => assignToFunction(f.id, user, false)}
      >
        <SkillIcon skill={f} /> {f.name}
      </Button>
    ))}
  </ButtonGroup>
);

const FunctionList = ({ functions, assignToFunction, user, multiFunction }) => {
  if (functions.length === 0)
    return <p className="small text-muted">{I18n.t('duty_planner.no_assignable_functions')}</p>;

  return (
    <div className="mb-3">
      <p className="small text-muted mt-3 mb-1">{I18n.t('duty_planner.click_to_assign')}</p>
      {multiFunction && <MultiFunctionSelector functions={functions} assignToFunction={assignToFunction} user={user} />}
      {!multiFunction && (
        <SingleFunctionSelector functions={functions} assignToFunction={assignToFunction} user={user} />
      )}
    </div>
  );
};

const StationSelector = ({ stations, selectedStationId, setSelectedStationId, user }) => {
  const [expanded, setExpanded] = useState(false);
  const selectedStation = stations.find(s => s.id === selectedStationId);

  if (!expanded) {
    return (
      <div className="d-flex align-items-center mt-3 mb-1">
        <span className="small text-muted">
          {I18n.t('duty_planner.assigning_to_station', { station: selectedStation?.name || '' })}
        </span>
        <Button variant="outline-secondary" size="sm" className="ms-auto py-0" onClick={() => setExpanded(true)}>
          {I18n.t('duty_planner.change_station')}
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-3 mb-2">
      <p className="small text-muted mb-1">{I18n.t('duty_planner.assign_to_station')}</p>
      <ToggleButtonGroup
        vertical
        className="w-100"
        size="sm"
        type="radio"
        name={`station-radio-${user.id}`}
        value={selectedStationId}
        onChange={value => {
          setSelectedStationId(value);
          setExpanded(false);
        }}
      >
        {stations.map(s => (
          <ToggleButton
            id={`station-btn-${user.id}-${s.id}`}
            variant="outline-secondary"
            type="radio"
            key={s.id}
            value={s.id}
          >
            {s.name}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </div>
  );
};

const canUserPerformFunction = (func, user) => {
  const functionSkillId = func.skill_id;
  if (!functionSkillId) return true;
  return user.intervals.some(i => (i.skill_ids || []).includes(functionSkillId));
};

const CandidateContainer = ({
  user,
  settings,
  assign,
  functions: ownFunctions,
  multiFunction: ownMultiFunction,
  stationId,
  periodStartTime,
  periodEndTime,
  isCandidate
}) => {
  const [displayActionList, setDisplayActionList] = useState(false);

  const { stations: allStations = [], stationSettings = {} } = useContext(DutyPlannerContext);
  const [selectedStationId, setSelectedStationId] = useState(stationId);
  const canSelectStation = isCandidate && allStations.length > 1 && stationId != null;
  const selectedSettings = canSelectStation ? stationSettings[selectedStationId] || {} : null;

  const targetStationId = canSelectStation ? selectedStationId : stationId;

  const functions = selectedSettings ? selectedSettings.functions : ownFunctions;
  const multiFunction = selectedSettings ? selectedSettings.allowMultipleFunctions : ownMultiFunction;
  const defaultFunctionId = selectedSettings ? selectedSettings.defaultFunctionId : settings?.defaultFunctionId;

  const performableFunctions = useMemo(() => {
    if (!functions) return [];
    return functions.filter(f => canUserPerformFunction(f, user));
  }, [functions, user]);

  const assignToFunction = (functionId, user, clearGaps) => {
    assign({ functionIds: functionId, defaultFunctionId, stationId: targetStationId, user, clearGaps });
  };

  const toggleFunctionList = e => {
    const target = e.target;
    if (target.id === 'duty_transaction_link') return;
    setDisplayActionList(!displayActionList);
  };

  const convertToCandidate = () => {
    assign({ functionIds: null, defaultFunctionId, stationId: targetStationId, user });
  };

  const onClickAction = isCandidate ? toggleFunctionList : convertToCandidate;

  const newAbsenceLink = linkToNewDutyTransaction('absence_requests', user.membershipId, {
    start_time: moment(periodStartTime).format(),
    end_time: moment(periodEndTime).format()
  });

  return (
    <div className="list-group-item">
      <div style={{ cursor: 'pointer' }} onClick={onClickAction}>
        <div>
          <span>{user.nickname}</span>{' '}
          <IntervalInformationPopup
            overlayPlacement={'right'}
            intervals={user.intervals}
            dutyTransactions={user.dutyTransactions}
          />
        </div>
        {isCandidate && (
          <MiniTimeline intervals={user.intervals} periodStartTime={periodStartTime} periodEndTime={periodEndTime} />
        )}
      </div>
      {displayActionList && (
        <>
          {canSelectStation && (
            <StationSelector
              stations={allStations}
              selectedStationId={selectedStationId}
              setSelectedStationId={setSelectedStationId}
              user={user}
            />
          )}
          <FunctionList
            multiFunction={multiFunction}
            functions={performableFunctions}
            user={user}
            assignToFunction={assignToFunction}
          />
          <a href={newAbsenceLink} target={'_blank'} rel="noreferrer">
            <IconCalendarXmark /> {I18n.t('schedules.exchanges.absence_request.new')}
          </a>
        </>
      )}
    </div>
  );
};

export default CandidateContainer;
